import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useMemo } from "react";

const STATUS_LABEL = {
  completed: "تکمیل شده",
  active: "مرحله فعلی",
  pending: "در انتظار",
};

const normalizeStages = (stages) => {
  if (!Array.isArray(stages)) return [];
  return stages
    .map((s, index) => ({
      order: Number(s.order ?? s.Order ?? index + 1),
      title: s.title || s.Title || `مرحله ${index + 1}`,
      completed: Boolean(s.completed ?? s.Completed),
      active: Boolean(s.active ?? s.Active),
      status: s.status || s.Status || "pending",
      detail: s.detail || s.Detail || "",
    }))
    .sort((a, b) => a.order - b.order);
};

/** Parse stages embedded in PartnerRejectionReason text (مراحل: / ✓ ● ○ lines). */
export const parseStagesFromPortalMessages = (messages) => {
  const text = String(messages || "");
  if (!text.includes("مراحل")) return [];
  const lines = text.split(/\r?\n/);
  const stages = [];
  for (const line of lines) {
    const m = line.match(
      /^\s*([✓✔●○•*]|\u25CF|\u25CB)?\s*(\d+)\.\s*(.+?)\s*$/u
    );
    if (!m) continue;
    const mark = m[1] || "";
    const completed = mark === "✓" || mark === "✔";
    const active = mark === "●" || mark === "\u25CF" || mark === "*";
    stages.push({
      order: Number(m[2]),
      title: m[3].trim(),
      completed,
      active,
      status: completed ? "completed" : active ? "active" : "pending",
    });
  }
  return stages;
};

const statusMeta = (item) => {
  if (item.completed || item.status === "completed") {
    return { key: "completed", color: "success", label: item.detail || STATUS_LABEL.completed };
  }
  if (item.active || item.status === "active") {
    return { key: "active", color: "warning", label: item.detail || STATUS_LABEL.active };
  }
  return { key: "pending", color: "default", label: item.detail || STATUS_LABEL.pending };
};

const BarqPortalStagesTimeline = ({
  stages = [],
  portalMessages = "",
  compact = false,
  showEmpty = false,
  emptyHint = "هنوز مراحل پنل برق‌پی دریافت نشده است. دکمه «بروزرسانی از برق‌پی» را بزنید.",
}) => {
  const items = useMemo(() => {
    const fromApi = normalizeStages(stages);
    if (fromApi.length) return fromApi;
    return parseStagesFromPortalMessages(portalMessages);
  }, [stages, portalMessages]);

  if (!items.length && !showEmpty) return null;

  const completedCount = items.filter((i) => i.completed || i.status === "completed").length;

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, borderRadius: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          مراحل و وضعیت در پنل برق‌پی
        </Typography>
        {items.length ? (
          <Chip
            size="small"
            variant="outlined"
            label={`${completedCount.toLocaleString("fa-IR")} از ${items.length.toLocaleString("fa-IR")} تکمیل`}
          />
        ) : null}
      </Stack>

      {!items.length ? (
        <Alert severity="info" sx={{ py: 0.5 }}>
          {emptyHint}
        </Alert>
      ) : (
        <Stack spacing={0} sx={{ position: "relative", pr: 1.5 }}>
          {items.map((item, index) => {
            const meta = statusMeta(item);
            return (
              <Stack
                key={`${item.order}-${item.title}`}
                direction="row"
                spacing={1.5}
                sx={{
                  position: "relative",
                  pb: index === items.length - 1 ? 0 : 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    pt: 0.25,
                  }}
                >
                  {meta.key === "completed" ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <FiberManualRecordIcon
                      color={meta.key === "active" ? "warning" : "disabled"}
                      fontSize="small"
                    />
                  )}
                  {index < items.length - 1 ? (
                    <Box
                      sx={{
                        flex: 1,
                        width: 2,
                        mt: 0.5,
                        bgcolor:
                          meta.key === "completed" ? "success.light" : "divider",
                        minHeight: 18,
                      }}
                    />
                  ) : null}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Typography
                      variant="body2"
                      fontWeight={meta.key === "active" ? 700 : 500}
                      color={
                        meta.key === "completed" ? "text.secondary" : "text.primary"
                      }
                    >
                      {item.order}. {item.title}
                    </Typography>
                    <Chip size="small" color={meta.color} label={meta.label} />
                  </Stack>
                </Box>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

BarqPortalStagesTimeline.propTypes = {
  stages: PropTypes.array,
  portalMessages: PropTypes.string,
  compact: PropTypes.bool,
  showEmpty: PropTypes.bool,
  emptyHint: PropTypes.string,
};

export default BarqPortalStagesTimeline;
